import type { LogPayload } from './types';

const REDACTED = '[REDACTED]';
const MAX_DEPTH = 8;

// Matched whole; the fragment list below would not catch 'cookie'.
const SENSITIVE_HEADERS = new Set([
  'authorization',
  'proxy-authorization',
  'cookie',
  'set-cookie',
  'x-api-key',
  'x-auth-token',
  'x-access-token',
]);

// Matched with separators stripped, so idToken and id_token both hit 'token'.
const SENSITIVE_FRAGMENTS = [
  'authorization',
  'token',
  'secret',
  'password',
  'passphrase',
  'apikey',
  'credential',
];

const isSensitiveKey = (key: string): boolean => {
  const lower = key.toLowerCase();
  if (SENSITIVE_HEADERS.has(lower)) return true;
  const normalized = lower.replace(/[^a-z0-9]/g, '');
  return SENSITIVE_FRAGMENTS.some((fragment) => normalized.includes(fragment));
};

const QUERY_PARAMS =
  'access_token|id_token|api_key|apikey|password|secret|signature|token|auth|key|sig';

export const scrubString = (value: string): string =>
  value
    // Rejected credentials come back inside error text, where no key rule reaches.
    .replace(
      /\b(Bearer|Basic)\s+([A-Za-z0-9._~+/=-]{8,})/gi,
      (_match, scheme) => `${scheme} ${REDACTED}`
    )
    .replace(
      new RegExp(`([?&](?:${QUERY_PARAMS})=)([^&#\\s]+)`, 'gi'),
      (_match, prefix) => `${prefix}${REDACTED}`
    );

// Assigning these names hits Object.prototype's setters: `out.__proto__ = x`
// re-parents the clone instead of recording a field.
const UNSAFE_KEYS = new Set(['__proto__', 'constructor', 'prototype']);

const assign = (
  target: Record<string, unknown>,
  key: string,
  value: unknown
): void => {
  if (UNSAFE_KEYS.has(key)) {
    Object.defineProperty(target, key, {
      value,
      enumerable: true,
      writable: true,
      configurable: true,
    });
    return;
  }
  target[key] = value;
};

// A throwing getter here would escape Logger.log.
const readProperty = (source: object, key: string): unknown => {
  try {
    return (source as Record<string, unknown>)[key];
  } catch {
    return '[Unreadable]';
  }
};

const redactValue = (
  value: unknown,
  depth: number,
  seen: WeakSet<object>
): unknown => {
  if (typeof value === 'string') return scrubString(value);
  if (value === null || typeof value !== 'object') return value;
  if (depth > MAX_DEPTH) return '[Max Depth Exceeded]';
  if (value instanceof Date) return value;
  // Ancestor path, not every object visited: apiV2 passes response.data as two
  // siblings, and marking the second '[Circular]' would drop the response body.
  if (seen.has(value)) return '[Circular]';
  seen.add(value);
  try {
    return redactObject(value, depth, seen);
  } finally {
    seen.delete(value);
  }
};

const redactObject = (
  value: object,
  depth: number,
  seen: WeakSet<object>
): unknown => {
  if (value instanceof Error) return redactError(value, depth, seen);
  if (Array.isArray(value)) {
    return value.map((item) => redactValue(item, depth + 1, seen));
  }

  // Object.entries would evaluate every getter outside the readProperty guard.
  const out: Record<string, unknown> = {};
  for (const key of Object.keys(value)) {
    assign(
      out,
      key,
      isSensitiveKey(key)
        ? REDACTED
        : redactValue(readProperty(value, key), depth + 1, seen)
    );
  }
  return out;
};

// Must stay an Error instance: PostHog captureException and datadogRum.addError
// both key their Error Tracking off one.
const redactError = (
  error: Error,
  depth: number,
  seen: WeakSet<object>
): Error => {
  const clean = new Error(scrubString(error.message));
  clean.name = error.name;
  if (error.stack) clean.stack = scrubString(error.stack);

  for (const key of Object.getOwnPropertyNames(error)) {
    if (key === 'name' || key === 'message' || key === 'stack') continue;
    assign(
      clean as unknown as Record<string, unknown>,
      key,
      isSensitiveKey(key)
        ? REDACTED
        : redactValue(readProperty(error, key), depth + 1, seen)
    );
  }

  return clean;
};

// Returns a clone; the axios interceptors re-throw the error they just logged.
export const redactPayload = (payload: LogPayload): LogPayload =>
  redactValue(payload, 0, new WeakSet()) as LogPayload;

export default redactPayload;
